import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import mailIcon from "@iconify-icons/lucide/mail";
import lockIcon from "@iconify-icons/lucide/lock";
import eyeIcon from "@iconify-icons/lucide/eye";
import eyeOffIcon from "@iconify-icons/lucide/eye-off";
import arrowRightIcon from "@iconify-icons/lucide/arrow-right";

import ModalErrors from "../modalErrors";
import { AuthContext } from "../../../context/context";
import { API, setSessionToken } from "../../../api/API";
import Cookies from "js-cookie";
import {
  startVerification,
  clearVerification,
} from "../../../core/infrastructure/security/deviceVerification";
import VerifyDeviceStep from "./VerifyDeviceStep";

/**
 * Formulario de login reutilizable — la MISMA pieza se pinta en la página
 * `/login` (wrapper propio) y dentro del `ModalHome` (que ya no navega, el
 * usuario entra sin cambiar de vista).
 *
 * Props opcionales:
 *  - `onSuccess()`: se llama tras login OK; por defecto navega a "/".
 *  - `onCancel()`: se llama al pulsar el enlace "Cancelar"; por defecto
 *    navega a "/". Cuando el formulario vive DENTRO del modal se pasa
 *    `toggleModal` para cerrarlo en lugar de navegar.
 *  - `compact`: layout más denso (menos padding) para caber en el modal.
 */
export default function LoginForm({ onSuccess, onCancel, compact = false }) {
  const { t } = useTranslation("global");
  const { setUserInfoQursiva, setIsLogged } = useContext(AuthContext);
  const navigate = useNavigate();
  const form = useRef(null);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    iboCode: 0,
  });
  const [errors, setErrors] = useState({ email: null, password: null });
  const [showPassword, setShowPassword] = useState(false);
  const [showModalError, setShowModalError] = useState(false);
  const [dataModalError, setDataModalError] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // FOC-492 — Step de la pantalla:
  //   "login"  → formulario de credenciales (default).
  //   "verify" → VerifyDeviceStep inline cuando el back pidió verificar un
  //              dispositivo nuevo (403 DEVICE_VERIFICATION_REQUIRED).
  const [step, setStep] = useState("login");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateLocal = () => {
    const next = { email: null, password: null };
    if (!loginData.email) next.email = t("login.emailRequired");
    else if (!/.+@.+\..+/.test(loginData.email)) next.email = t("login.emailInvalid");
    if (!loginData.password) next.password = t("login.passwordRequired");
    else if (loginData.password.length < 6) next.password = t("login.passwordMin");
    setErrors(next);
    return !next.email && !next.password;
  };

  // FOC-492 — Lógica compartida del post-login: la usan el submit normal y
  // el callback onVerified del VerifyDeviceStep (ambos recibe el MISMO payload
  // del back: user, token, ibo, allowed, menu).
  const applyAuthData = (data) => {
    console.log("[academy-auth] applyAuthData ENTRY", {
      hasData: !!data,
      hasUser: !!data?.user,
      hasToken: !!data?.token,
      tokenLen: data?.token?.length,
      userType: data?.user?.userType,
    });
    if (!data || !data.user) {
      console.warn("[academy-auth] applyAuthData: data/data.user falsy, returning");
      return false;
    }
    if (data.user.userType === 2) {
      console.warn("[academy-auth] applyAuthData: userType=2, blocking");
      setDataModalError({
        modalTitle: t("login.errorModal"),
        modalBody: t("login.error"),
      });
      setShowModalError(true);
      return false;
    }

    // Guardamos token en memoria PRIMERO (sincronico, inmediato).
    setSessionToken(data.token);

    // Cookie. Las opciones por default de js-cookie 3.x usan path=/ y sin
    // dominio (= host actual). SameSite va por default Lax. Para asegurarnos
    // pasamos path='/' explicito.
    Cookies.set("user-tk", data.token, { path: "/" });

    const merged = {
      ...data.ibo,
      ...data.user,
      id: data.user.userId,
      tokenQursiva: data.token,
    };
    localStorage.setItem("userInfoQursiva", JSON.stringify(merged));
    setUserInfoQursiva(merged);
    setIsLogged(true);

    // Verificar que las escrituras persistieron. Si alguna fallo, loguear.
    const cookieCheck = Cookies.get("user-tk");
    const lsCheck = localStorage.getItem("userInfoQursiva");
    console.log("[academy-auth] applyAuthData POST-WRITE", {
      memory: "set",
      cookieWritten: !!cookieCheck,
      cookieLen: cookieCheck?.length,
      lsWritten: !!lsCheck,
      lsHasToken: lsCheck ? !!JSON.parse(lsCheck)?.tokenQursiva : false,
    });

    if (onSuccess) onSuccess();
    else navigate("/");
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateLocal()) return;
    setSubmitting(true);
    try {
      const resultLogin = await API()
        .post("user/authentication", loginData)
        .catch((error) => (error.response ? error.response : { error }));

      // FOC-492 — Tres códigos nuevos del back que requieren acciones
      // específicas, no un modal genérico de error.
      if (resultLogin.status === 403) {
        const code = resultLogin.data && resultLogin.data.code;
        if (code === "DEVICE_VERIFICATION_REQUIRED") {
          const payload = resultLogin.data && resultLogin.data.data;
          if (payload && payload.verificationToken && payload.maskedEmail) {
            startVerification({
              verificationToken: payload.verificationToken,
              maskedEmail: payload.maskedEmail,
              expiresIn: Number(payload.expiresIn || 900),
              resendAvailableAt: payload.resendAvailableAt,
            });
            setStep("verify");
            return;
          }
        }
        if (code === "ACCOUNT_BLOCKED") {
          setDataModalError({
            modalTitle: t("login.errorModal"),
            modalBody: t("login.accountBlocked"),
          });
          setShowModalError(true);
          return;
        }
        if (code === "DEVICE_VERIFICATION_UNAVAILABLE") {
          setDataModalError({
            modalTitle: t("login.errorModal"),
            modalBody: t("login.verificationUnavailable"),
          });
          setShowModalError(true);
          return;
        }
      }

      if (resultLogin.status !== 200) {
        setDataModalError({
          modalTitle: t("login.errorModal"),
          modalBody: resultLogin.data
            ? resultLogin?.data?.data?.signin?.[0]
            : "Ocurrió un error en la conexión al servidor, por favor intenta nuevamente.",
        });
        setShowModalError(true);
        return;
      }

      applyAuthData(resultLogin.data.data);
    } finally {
      setSubmitting(false);
    }
  };

  const inputWrap = (hasError) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "var(--color-surface)",
    border: `1.5px solid ${hasError ? "var(--color-danger)" : "var(--color-line-strong)"}`,
    borderRadius: 11,
    padding: "0 14px",
    minHeight: compact ? 48 : 52,
  });
  const inputStyle = {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: "none",
    background: "transparent",
    fontSize: 16,
    fontWeight: 600,
    color: "var(--color-ink)",
  };
  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 800,
    color: "var(--color-ink)",
    marginBottom: 6,
  };
  const errorStyle = {
    marginTop: 6,
    fontSize: 12.5,
    fontWeight: 700,
    color: "var(--color-danger)",
  };

  if (step === "verify") {
    return (
      <>
        <VerifyDeviceStep
          onVerified={(data) => {
            // Limpiar el token temporal (purpose=device_verification) en
            // memoria apenas termina el verify OK. Si no, el token temporal
            // queda vivo junto con el token real y getUserToken() cae a él
            // ante cualquier race con la cookie `user-tk` recién seteada.
            // El gateway rechaza el temporal fuera de PURPOSE_ROUTES con
            // TOKEN_INVALID (sintoma: 401 esporadicos tras login OK).
            clearVerification();
            setStep("login");
            applyAuthData(data);
          }}
          onBackToLogin={(messageKey) => {
            clearVerification();
            setStep("login");
            if (messageKey) {
              setDataModalError({
                modalTitle: t("login.errorModal"),
                modalBody: t(messageKey),
              });
              setShowModalError(true);
            }
          }}
        />
        {showModalError && (
          <ModalErrors
            handleClose={setShowModalError}
            modalBody={dataModalError.modalBody}
            modalTitle={dataModalError.modalTitle}
          />
        )}
      </>
    );
  }

  return (
    <>
      <form ref={form} onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="login-email" style={labelStyle}>
            {t("login.email")}
          </label>
          <label htmlFor="login-email" style={inputWrap(!!errors.email)}>
            <Icon
              icon={mailIcon}
              width={20}
              height={20}
              aria-hidden="true"
              style={{ color: "var(--color-ink-2)", flexShrink: 0 }}
            />
            <input
              id="login-email"
              name="email"
              type="email"
              required
              placeholder={t("login.emailPlaceholder")}
              value={loginData.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "login-email-err" : undefined}
              style={inputStyle}
              autoComplete="username"
            />
          </label>
          {errors.email && (
            <div id="login-email-err" role="alert" style={errorStyle}>
              {errors.email}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label htmlFor="login-password" style={labelStyle}>
            {t("login.password")}
          </label>
          <label htmlFor="login-password" style={inputWrap(!!errors.password)}>
            <Icon
              icon={lockIcon}
              width={20}
              height={20}
              aria-hidden="true"
              style={{ color: "var(--color-ink-2)", flexShrink: 0 }}
            />
            <input
              id="login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              required
              minLength={6}
              placeholder={t("login.passwordPlaceholder")}
              value={loginData.password}
              onChange={handleChange}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "login-password-err" : undefined}
              style={inputStyle}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword ? t("login.hidePassword") : t("login.showPassword")
              }
              aria-pressed={showPassword}
              style={{
                border: 0,
                background: "transparent",
                padding: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "var(--color-ink-2)",
                flexShrink: 0,
              }}
            >
              <Icon
                icon={showPassword ? eyeOffIcon : eyeIcon}
                width={20}
                height={20}
                aria-hidden="true"
              />
            </button>
          </label>
          {errors.password && (
            <div id="login-password-err" role="alert" style={errorStyle}>
              {errors.password}
            </div>
          )}
        </div>

        <div style={{ textAlign: "right", marginBottom: 22 }}>
          <a
            href="https://lyra.visionbiz.net/forgot-password"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--color-accent-text)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            {t("login.forgot")}
          </a>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary w-full flex items-center justify-center gap-s2"
          style={{
            minHeight: compact ? 48 : 52,
            fontSize: 16,
            fontWeight: 800,
            opacity: submitting ? 0.7 : 1,
            cursor: submitting ? "wait" : "pointer",
          }}
        >
          {submitting ? t("login.signingIn") : t("login.buttonLogin")}
          {!submitting && (
            <Icon icon={arrowRightIcon} width={18} height={18} aria-hidden="true" />
          )}
        </button>

        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button
            type="button"
            onClick={() => (onCancel ? onCancel() : navigate("/"))}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              fontSize: 14,
              fontWeight: 700,
              color: "var(--color-ink-2)",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {t("login.cancel")}
          </button>
        </div>
      </form>

      {showModalError && (
        <ModalErrors
          handleClose={setShowModalError}
          modalBody={dataModalError.modalBody}
          modalTitle={dataModalError.modalTitle}
        />
      )}
    </>
  );
}
